/**
 * 分页器（03-UIUX.md §6.4）。
 *
 * 规范要求游标分页、不显示总页数。当前 `GET /admin/licenses` 的契约是
 * `page` + `limit` + `total`（openapi PageMeta），因此这里**按契约实现页码分页**，
 * 但仍遵守「不承诺精确总数」的展示纪律：超过 10,000 条只显示区间与「> 10,000」。
 * 契约与设计的这处差异已作为待办上报，不在前端私自伪造游标。
 */
import { Icon } from '../icons/Icon';
import { ICON_SIZE } from '../tokens';
import { formatCount, formatTotal } from '../lib/format';

export const PAGE_SIZES = [25, 50, 100, 200] as const;
export type PageSize = (typeof PAGE_SIZES)[number];
export const DEFAULT_PAGE_SIZE: PageSize = 50;

export interface PagerProps {
  readonly page: number;
  readonly limit: PageSize;
  readonly total: number;
  readonly hasMore: boolean;
  readonly onPageChange: (page: number) => void;
  readonly onLimitChange: (limit: PageSize) => void;
}

export function Pager({ page, limit, total, hasMore, onPageChange, onLimitChange }: PagerProps) {
  const first = total === 0 ? 0 : (page - 1) * limit + 1;
  const last = Math.min(page * limit, total);

  return (
    <div className="pager">
      <label className="cell-inline">
        每页
        <select
          value={limit}
          onChange={(event) => onLimitChange(Number(event.target.value) as PageSize)}
          aria-label="每页条数"
        >
          {PAGE_SIZES.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
        条
      </label>

      <span>
        显示第 {formatCount(first)} – {formatCount(last)} 条，共 {formatTotal(total)}
      </span>

      <span className="pager__spacer" />

      <button
        type="button"
        className="btn"
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
      >
        <Icon name="chevron-right" size={ICON_SIZE.inline} className="pager__prev-icon" />
        上一页
      </button>
      <button
        type="button"
        className="btn"
        onClick={() => onPageChange(page + 1)}
        disabled={!hasMore}
      >
        下一页
        <Icon name="chevron-right" size={ICON_SIZE.inline} />
      </button>
    </div>
  );
}
